// Checkbox
// ---------
// Native checkbox styled to the standard. Works with controlled `checked` and
// `onChange`. Use the `label` prop for the most common case (label to the right
// of the box), or omit it and provide your own label structure if you need
// custom layout.
//
// Variants:
//   - light (default): blue accent on white background
//   - dark:            blue accent on translucent dark background
//
// Usage:
//   <Checkbox checked={value} onChange={(e) => setValue(e.target.checked)} label="Use TLS" />
//   <Checkbox checked={value} onChange={...} />  // bare, render label yourself

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const checkboxVariants = cva(
  "h-4 w-4 rounded shrink-0 cursor-pointer focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        light: "border-gray-300 text-blue-600 focus:ring-blue-500",
        dark: "border-white/20 bg-white/[0.04] text-blue-500 focus:ring-blue-400",
      },
    },
    defaultVariants: {
      variant: "light",
    },
  }
);

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size">,
    VariantProps<typeof checkboxVariants> {
  label?: React.ReactNode;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, variant, label, id, ...props }, ref) => {
    // Generate a stable id when one isn't provided so the label `htmlFor` works.
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const labelColor = variant === "dark" ? "text-white/80" : "text-gray-700";

    const inputElement = (
      <input
        type="checkbox"
        ref={ref}
        id={inputId}
        className={cn(checkboxVariants({ variant }), className)}
        {...props}
      />
    );

    if (!label) {
      return inputElement;
    }

    return (
      <label
        htmlFor={inputId}
        className={cn("flex items-center gap-2 text-sm cursor-pointer select-none", labelColor)}
      >
        {inputElement}
        <span>{label}</span>
      </label>
    );
  }
);
Checkbox.displayName = "Checkbox";

export { Checkbox, checkboxVariants };
